<script>
import { h, Transition, toHandlers } from 'vue';
import Popper from 'popper.js';
import { popoversMixin } from '../utils/popovers';
import { on, off, elementContains } from '../utils/helpers';
import { addTapOrClickHandler } from '../utils/touch';
import { isFunction } from '../utils/_';

export default {
  name: 'Popover',
  render() {
    return h(
      'div',
      {
        class: [
          'vc-popover-content-wrapper',
          { 'is-interactive': this.isInteractive },
        ],
        ref: 'popover',
      },
      [
        h(
          Transition,
          {
            name: this.transition,
            appear: true,
            ...toHandlers({
              beforeEnter: this.beforeEnter,
              afterEnter: this.afterEnter,
              beforeLeave: this.beforeLeave,
              afterLeave: this.afterLeave,
            }),
          },
          {
            default: () =>
              this.isVisible
                ? h(
                    'div',
                    {
                      tabindex: -1,
                      class: [
                        'vc-popover-content',
                        `direction-${this.direction}`,
                        this.contentClass,
                      ],
                    },
                    [
                      this.renderContent(),
                      h('span', {
                        class: [
                          'vc-popover-caret',
                          `direction-${this.direction}`,
                          `align-${this.alignment}`,
                        ],
                      }),
                    ],
                  )
                : null,
          },
        ),
      ],
    );
  },
  mixins: [popoversMixin],
  props: {
    id: { type: String, required: true },
    transition: { type: String, default: 'slide-fade' },
    contentClass: String,
  },
  data() {
    return {
      ref: null,
      args: null,
      visibility: '',
      placement: 'bottom',
      positionFixed: false,
      modifiers: {},
      isInteractive: false,
      delay: 10,
      popperEl: null,
    };
  },
  computed: {
    popperOptions() {
      return {
        placement: this.placement,
        positionFixed: this.positionFixed,
        modifiers: {
          hide: { enabled: false },
          preventOverflow: { enabled: false },
          ...this.modifiers,
        },
        onCreate: this.onPopperUpdate,
        onUpdate: this.onPopperUpdate,
      };
    },
    isVisible() {
      return !!(this.ref && this.$slots.default && this.visibility !== 'hidden');
    },
    direction() {
      return (this.placement && this.placement.split('-')[0]) || 'bottom';
    },
    alignment() {
      const isLeftRight =
        this.direction === 'left' || this.direction === 'right';
      let alignment = this.placement.split('-');
      alignment = alignment.length > 1 ? alignment[1] : '';
      if (['start', 'top', 'left'].includes(alignment)) {
        return isLeftRight ? 'top' : 'left';
      }
      if (['end', 'bottom', 'right'].includes(alignment)) {
        return isLeftRight ? 'bottom' : 'right';
      }
      return isLeftRight ? 'middle' : 'center';
    },
    state() {
      return this.$popovers[this.id];
    },
  },
  watch: {
    state: {
      immediate: true,
      handler(val) {
        if (val) {
          this.ref = val.ref;
          this.args = val.args;
          this.visibility = val.visibility;
          this.placement = val.placement;
          this.positionFixed = val.positionFixed;
          this.modifiers = val.modifiers;
          this.isInteractive = val.isInteractive;
          this.setupPopper();
        }
      },
    },
  },
  mounted() {
    this.popoverEl = this.$refs.popover;
    this.addEvents();
  },
  beforeUnmount() {
    this.removeEvents();
    this.destroyPopper();
  },
  methods: {
    renderContent() {
      const slot = this.$slots.default
      if (!isFunction(slot)) {
        return null
      }

      return slot({
        direction: this.direction,
        alignment: this.alignment,
        args: this.args,
        updateLayout: this.scheduleUpdate,
        hide: opts => this.hide(opts),
      })
    },
    addEvents() {
      on(this.popoverEl, 'click', this.onClick);
      on(this.popoverEl, 'mouseover', this.onMouseOver);
      on(this.popoverEl, 'mouseleave', this.onMouseLeave);
      on(this.popoverEl, 'focusin', this.onFocusIn);
      on(this.popoverEl, 'focusout', this.onFocusOut);
      on(document, 'keydown', this.onDocumentKeydown);
      this.removeDocHandler = addTapOrClickHandler(
        document,
        this.onDocumentClick,
      );
    },
    removeEvents() {
      off(this.popoverEl, 'click', this.onClick);
      off(this.popoverEl, 'mouseover', this.onMouseOver);
      off(this.popoverEl, 'mouseleave', this.onMouseLeave);
      off(this.popoverEl, 'focusin', this.onFocusIn);
      off(this.popoverEl, 'focusout', this.onFocusOut);
      off(document, 'keydown', this.onDocumentKeydown);
      if (this.removeDocHandler) this.removeDocHandler();
    },
    onClick(e) {
      e.stopPropagation();
    },
    onMouseOver() {
      if (this.isInteractive && this.visibility === 'hover') {
        this.show();
      }
    },
    onMouseLeave() {
      if (this.isInteractive && this.visibility === 'hover') {
        this.hide();
      }
    },
    onFocusIn() {
      if (this.isInteractive && this.visibility === 'focus') {
        this.show();
      }
    },
    onFocusOut(e) {
      if (
        this.isInteractive &&
        this.visibility === 'focus' &&
        e.relatedTarget &&
        !elementContains(this.popoverEl, e.relatedTarget)
      ) {
        this.hide();
      }
    },
    onDocumentClick(e) {
      if (!this.$refs.popover || !this.ref) {
        return;
      }
      // Don't hide if target element is contained within popover ref or content
      if (
        elementContains(this.popoverEl, e.target) ||
        elementContains(this.ref, e.target)
      ) {
        return;
      }
      // Hide the popover
      this.hide();
    },
    onDocumentKeydown(e) {
      if (e.key === 'Esc' || e.key === 'Escape') {
        this.hide();
      }
    },
    show() {
      this.$showPopover({ id: this.id, ref: this.ref, delay: 0 });
    },
    hide(opts) {
      this.$hidePopover({
        ...opts,
        id: this.id,
        ref: this.ref,
      });
    },
    onUpdate({ args }) {
      this.args = args;
      this.setupPopper();
    },
    setupPopper() {
      this.$nextTick(() => {
        if (!this.ref || !this.$refs.popover) return;
        if (this.popper && this.popper.reference !== this.ref) {
          this.popper.destroy();
          this.popper = null;
        }
        if (!this.popper) {
          this.popper = new Popper(
            this.ref,
            this.popoverEl,
            this.popperOptions,
          );
        } else {
          this.popper.scheduleUpdate();
        }
      });
    },
    onPopperUpdate(data) {
      this.placement = data.placement;
    },
    scheduleUpdate() {
      if (this.popper) {
        this.popper.scheduleUpdate();
      }
    },
    beforeEnter(e) {
      this.$emit('beforeShow', e);
    },
    afterEnter(e) {
      this.$emit('afterShow', e);
    },
    beforeLeave(e) {
      this.$emit('beforeHide', e);
    },
    afterLeave(e) {
      this.destroyPopper();
      this.$emit('afterHide', e);
    },
    destroyPopper() {
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.vc-popover-content-wrapper {
  --popover-horizontal-content-offset: 8px;
  --popover-vertical-content-offset: 10px;
  --popover-slide-translation: 15px;
  --popover-transition-time: 0.14s ease-in-out;
  --popover-caret-horizontal-offset: 18px;
  --popover-caret-vertical-offset: 8px;

  position: absolute;
  display: block;
  outline: none;
  z-index: 10;
}

.vc-popover-content-wrapper:not(.is-interactive) {
  pointer-events: none;
}

.vc-popover-content {
  position: relative;
  outline: none;
  z-index: 10;
}

.vc-popover-content.direction-bottom {
  margin-top: var(--popover-vertical-content-offset);
}

.vc-popover-content.direction-top {
  margin-bottom: var(--popover-vertical-content-offset);
}

.vc-popover-content.direction-left {
  margin-right: var(--popover-horizontal-content-offset);
}

.vc-popover-content.direction-right {
  margin-left: var(--popover-horizontal-content-offset);
}

.vc-popover-caret {
  content: '';
  position: absolute;
  display: block;
  width: 12px;
  height: 12px;
  border-top: inherit;
  border-left: inherit;
  background: inherit;
  z-index: -1;
}

.vc-popover-caret.direction-bottom {
  top: 0;
}

.vc-popover-caret.direction-bottom.align-left {
  transform: translateY(-50%) rotate(45deg);
}

.vc-popover-caret.direction-bottom.align-center {
  transform: translateX(-50%) translateY(-50%) rotate(45deg);
}

.vc-popover-caret.direction-bottom.align-right {
  transform: translateY(-50%) rotate(45deg);
}

.vc-popover-caret.direction-top {
  top: 100%;
}

.vc-popover-caret.direction-top.align-left {
  transform: translateY(-50%) rotate(-135deg);
}

.vc-popover-caret.direction-top.align-center {
  transform: translateX(-50%) translateY(-50%) rotate(-135deg);
}

.vc-popover-caret.direction-top.align-right {
  transform: translateY(-50%) rotate(-135deg);
}

.vc-popover-caret.direction-left {
  left: 100%;
}

.vc-popover-caret.direction-left.align-top {
  transform: translateX(-50%) rotate(135deg);
}

.vc-popover-caret.direction-left.align-middle {
  transform: translateY(-50%) translateX(-50%) rotate(135deg);
}

.vc-popover-caret.direction-left.align-bottom {
  transform: translateX(-50%) rotate(135deg);
}

.vc-popover-caret.direction-right {
  left: 0;
}

.vc-popover-caret.direction-right.align-top {
  transform: translateX(-50%) rotate(-45deg);
}

.vc-popover-caret.direction-right.align-middle {
  transform: translateY(-50%) translateX(-50%) rotate(-45deg);
}

.vc-popover-caret.direction-right.align-bottom {
  transform: translateX(-50%) rotate(-45deg);
}

.vc-popover-caret.align-left {
  left: var(--popover-caret-horizontal-offset);
}

.vc-popover-caret.align-center {
  left: 50%;
}

.vc-popover-caret.align-right {
  right: var(--popover-caret-horizontal-offset);
}

.vc-popover-caret.align-top {
  top: var(--popover-caret-vertical-offset);
}

.vc-popover-caret.align-middle {
  top: 50%;
}

.vc-popover-caret.align-bottom {
  bottom: var(--popover-caret-vertical-offset);
}

.fade-enter-active,
.fade-leave-active,
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all var(--popover-transition-time);
  pointer-events: none;
}

.fade-enter,
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter,
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
}

.slide-fade-enter.direction-bottom,
.slide-fade-enter-from.direction-bottom,
.slide-fade-leave-to.direction-bottom {
  transform: translateY(calc(-1 * var(--popover-slide-translation)));
}

.slide-fade-enter.direction-top,
.slide-fade-enter-from.direction-top,
.slide-fade-leave-to.direction-top {
  transform: translateY(var(--popover-slide-translation));
}

.slide-fade-enter.direction-left,
.slide-fade-enter-from.direction-left,
.slide-fade-leave-to.direction-left {
  transform: translateX(var(--popover-slide-translation));
}

.slide-fade-enter.direction-right,
.slide-fade-enter-from.direction-right,
.slide-fade-leave-to.direction-right {
  transform: translateX(calc(-1 * var(--popover-slide-translation)));
}
</style>
